import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { getProductList } from 'helpers/backend_helper';
import { printBarcodes } from 'utils/ScannerUtil';
import { Loader2 } from 'lucide-react';

interface ExcelRow {
  SKU: string;
  Pieces: number;
  packOf?: number;
  TotalBarcodes?: number;
}

interface BarcodeData {
  sku: string;
  code?: string;
  sizeInfo?: string;
  quantity?: number;
  extraInfo?: string;
  productName?: string;
  unitPrice?: string;
}

export const BarcodeGenerator = () => {
  const [excelData, setExcelData] = useState<ExcelRow[]>([]);
  const [productList, setProductList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [barcodeList, setBarcodeList] = useState<BarcodeData[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setIsLoading(true);
      const response = await getProductList();
      if (response.success) {
        setProductList(response.data);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error loading products:', error);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const workbook = XLSX.read(event.target?.result, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json<ExcelRow>(sheet);

        // Add packOf and calculate TotalBarcodes for each row
        const enhancedData = data.map(row => {
          const product = productList.find(p => 
            p.productsProductvariants?.some((variant: any) => variant.sku === row.SKU)
          );
          
          if (product) {
            const variant = product.productsProductvariants.find((v: any) => v.sku === row.SKU);
            const packOf = variant?.packOf || 0;
            const totalBarcodes = Math.ceil(row.Pieces / packOf);
            
            return {
              ...row,
              packOf,
              TotalBarcodes: totalBarcodes
            };
          }
          
          return {
            ...row,
            packOf: 0,
            TotalBarcodes: 0
          };
        });

        setExcelData(enhancedData);
      } catch (error) {
        console.error('Error reading Excel file:', error);
      }
    };
    reader.readAsBinaryString(file);
  };

  const generateBarcodes = () => {
    try {
      const barcodes: BarcodeData[] = [];

      excelData.forEach(row => {
        // Find product variant with matching SKU
        const product = productList.find(p => 
          p.productsProductvariants?.some((variant: any) => variant.sku === row.SKU)
        );
        
        if (product) {
          const variant = product.productsProductvariants.find((v: any) => v.sku === row.SKU);
          
          // Use the pre-calculated TotalBarcodes from excelData
          for (let i = 0; i < (row.TotalBarcodes || 0); i++) {
            barcodes.push({
              sku: variant.sku,
              code: variant.sku,
              sizeInfo: variant.productsizesProductvariants?.name || '',
              quantity: variant.packOf || 0,
              unitPrice: variant.unitPrice.toString() || '0',
              extraInfo: `${new Date().getFullYear().toString().slice(-2)}${parseInt(variant.unitPrice) || 0}${new Date().getFullYear().toString().slice(-2)}`,
              productName: product.name || '',
            });
          }
        }
      });

      setBarcodeList(barcodes);
    } catch (error) {
      console.error('Error generating barcodes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateBarcodes();
  }, [excelData]);

  const handlePrint = () => {
    if (barcodeList.length > 0) {
      printBarcodes(barcodeList, 'Barcode Print');
    }
  };

  const handleClear = () => {
    setExcelData([]);
    setBarcodeList([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Barcode Generator</h1>
      
      <div className="mb-4 flex items-center gap-4">
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
          className="mb-4"
        />

        {barcodeList.length > 0 && (  
          <>
            <button
              onClick={handlePrint}
              className="px-4 py-1 bg-green-500 text-white rounded hover:bg-green-600"  
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="animate-spin" /> : 'Print Barcodes'}
            </button>

            <button
              onClick={handleClear}
              className="px-4 py-1 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Clear All
            </button>
          </>
        )}
      </div>

      {excelData.length > 0 && (
        <div className="mb-4">
          <div className="overflow-x-auto overflow-y-auto max-h-[550px] max-w-[420px]">
            <table className="min-w-[400px] border bg-white">
              <thead>
                <tr>
                  <th className="border p-2 bg-gray-50">SKU</th>
                  <th className="border p-2 bg-gray-50">Pieces</th>
                  <th className="border p-2 bg-gray-50">Pack Of</th>
                  <th className="border p-2 bg-gray-50">Total Barcodes</th>
                </tr>
              </thead>
              <tbody>
                {excelData.map((row, index) => (
                  <tr key={index}>
                    <td className="border p-2">{row.SKU}</td>
                    <td className="border p-2">{row.Pieces}</td>
                    <td className="border p-2">{row.packOf}</td>
                    <td className="border p-2">{row.TotalBarcodes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}; 